export interface ServiceWorkerUpdateHandle {
  /** True when a waiting worker, or a newly controlling worker, can be applied safely. */
  readonly pending: boolean;
  /** Suitable for React useSyncExternalStore or an equivalent update-ready notice. */
  subscribe(listener: () => void): () => void;
  /** Call only after active reading progress and outbox state are durable. */
  activate(): Promise<void>;
  /** Every tab decides independently whether its current UI may reload. */
  setSafeToReload(safe: boolean): void;
}

interface ServiceWorkerUpdateCoordinatorOptions {
  activateWaitingWorker(): Promise<void>;
  reload(): void;
}

export interface ServiceWorkerUpdateCoordinator {
  readonly handle: ServiceWorkerUpdateHandle;
  stage(): void;
  workerControlling(): void;
}

declare global {
  interface Window {
    imprintServiceWorkerUpdate?: ServiceWorkerUpdateHandle;
  }
}

export function createServiceWorkerUpdateCoordinator({
  activateWaitingWorker,
  reload,
}: ServiceWorkerUpdateCoordinatorOptions): ServiceWorkerUpdateCoordinator {
  const listeners = new Set<() => void>();
  let pending = false;
  let activationRequested = false;
  let workerControlsPage = false;
  let activationPromise: Promise<void> | undefined;
  let reloading = false;
  let safeToReload = false;

  const notify = () => listeners.forEach((listener) => listener());
  const setPending = (value: boolean) => {
    if (pending === value) return;
    pending = value;
    notify();
  };
  const reloadOnce = () => {
    if (reloading || !safeToReload) return;
    reloading = true;
    setPending(false);
    reload();
  };

  const handle: ServiceWorkerUpdateHandle = {
    get pending() { return pending; },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setSafeToReload(safe) {
      safeToReload = safe;
      if (safe && activationRequested && workerControlsPage) reloadOnce();
    },
    async activate() {
      if (!pending || reloading) return;
      activationRequested = true;
      if (workerControlsPage) {
        reloadOnce();
        return;
      }
      if (!activationPromise) {
        activationPromise = Promise.resolve()
          .then(activateWaitingWorker)
          .catch((error) => {
            activationRequested = false;
            activationPromise = undefined;
            throw error;
          });
      }
      await activationPromise;
    },
  };

  return {
    handle,
    stage() { setPending(true); },
    workerControlling() {
      if (reloading) return;
      workerControlsPage = true;
      setPending(true);
      if (activationRequested) reloadOnce();
    },
  };
}
